import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import NewsCard from '@/components/base/NewsCard';
import EmptyState from '@/components/base/EmptyState';
import { movies } from '@/mocks/movies';
import { newsList } from '@/mocks/news';
import { genreNameMap } from '@/mocks/genres';
import { usePageMeta } from '@/hooks/usePageMeta';

const hotWords = ['银河彼岸', '科幻', '悬疑', '票房', '动画', '幕后'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(initial);
  const [tab, setTab] = useState<'movie' | 'news'>('movie');

  usePageMeta({
    title: initial ? `${initial} - 搜索 - 草民电影` : '影片搜索 - 草民电影',
    description: '在草民电影搜索影片与资讯，支持关键词匹配与内容类型切换，快速找到你想要的电影信息。',
  });

  useEffect(() => {
    setKeyword(searchParams.get('q') ?? '');
  }, [searchParams]);

  const trimmed = keyword.trim();

  const matchedMovies = useMemo(() => {
    if (!trimmed) return [];
    const lower = trimmed.toLowerCase();
    return movies.filter((movie) => {
      const genreText = movie.genres.map((g) => genreNameMap[g] ?? g).join(' ');
      return (
        movie.title.toLowerCase().includes(lower) ||
        movie.titleEn.toLowerCase().includes(lower) ||
        movie.director.toLowerCase().includes(lower) ||
        movie.cast.join(' ').toLowerCase().includes(lower) ||
        movie.region.toLowerCase().includes(lower) ||
        genreText.toLowerCase().includes(lower)
      );
    });
  }, [trimmed]);

  const matchedNews = useMemo(() => {
    if (!trimmed) return [];
    const lower = trimmed.toLowerCase();
    return newsList.filter(
      (item) =>
        item.title.toLowerCase().includes(lower) ||
        item.summary.toLowerCase().includes(lower) ||
        item.category.toLowerCase().includes(lower),
    );
  }, [trimmed]);

  const results = tab === 'movie' ? matchedMovies : matchedNews;
  const totalCount = matchedMovies.length + matchedNews.length;

  const submitSearch = (value: string) => {
    setKeyword(value);
    const next = value.trim();
    setSearchParams(next ? { q: next } : {});
  };

  const recommendations = useMemo(() => [...movies].sort((a, b) => b.rating - a.rating).slice(0, 4), []);

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索影片与资讯</h1>
        <p className="mt-2 text-sm text-foreground-500">输入影片名称、类型、导演或资讯关键词即可搜索。</p>
      </header>

      <form
        className="mt-5 flex flex-col gap-3 sm:flex-row"
        onSubmit={(event) => {
          event.preventDefault();
          submitSearch(keyword);
        }}
      >
        <div className="relative flex-1">
          <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索影片、类型或资讯"
            className="h-12 w-full rounded-full border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-line" /> 搜索
        </button>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-400">热门搜索：</span>
        {hotWords.map((word) => (
          <button
            key={word}
            type="button"
            onClick={() => submitSearch(word)}
            className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {word}
          </button>
        ))}
      </div>

      {trimmed && (
        <>
          <p className="mt-8 text-sm text-foreground-600">
            搜索「<span className="font-semibold text-primary-700">{trimmed}</span>」，共找到{' '}
            <span className="font-semibold text-accent-600">{totalCount}</span> 条结果
          </p>

          <div className="mt-4 flex items-center gap-2 border-b border-background-200">
            <button
              type="button"
              onClick={() => setTab('movie')}
              className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === 'movie'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-foreground-500 hover:text-foreground-800'
              }`}
            >
              电影 ({matchedMovies.length})
            </button>
            <button
              type="button"
              onClick={() => setTab('news')}
              className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === 'news'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-foreground-500 hover:text-foreground-800'
              }`}
            >
              资讯 ({matchedNews.length})
            </button>
          </div>

          <div className="mt-6">
            {results.length === 0 ? (
              <EmptyState
                title="未找到相关内容"
                message={`没有与「${trimmed}」匹配的结果，换个关键词试试，或看看下面的高分推荐。`}
                icon="ri-search-eye-line"
              />
            ) : tab === 'movie' ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {matchedMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {matchedNews.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {(!trimmed || results.length === 0) && (
        <section className="mt-12">
          <h2 className="mb-6 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <span className="inline-block h-5 w-1 rounded-full bg-accent-500" />
            高分影片推荐
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {recommendations.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}